import type { ChangeEvent, FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Checkbox } from '@chakra-ui/react';
import { defineMessage, useIntl } from 'react-intl';
import { ListReveal } from '../../../../../components/list-reveal/ListReveal';
import { Accordion } from '../../../../../ui-components/accordion/Accordion';
import { AccordionItem } from '../../../../../ui-components/accordion/AccordionItem';
import type { CategoryNode, UseSearchResult } from '../../../hooks/use-search';

interface CategoryFilterSectionProps {
    search: UseSearchResult;
}

const allCategory = defineMessage({
    id: 'search.filters.category.all-category',
    defaultMessage: 'All {categoryName}',
});

export const CategoryFilterSection: FC<CategoryFilterSectionProps> = ({
    search: {
        filters,
        onChangeFilter,
        isFilterSelected,
        onBulkChangeFilter,
        categoriesMap,
        activeFilters,
    },
}) => {
    const intl = useIntl();

    const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);

    const fullCategories = useMemo(
        () =>
            (filters?.categories ?? []).map((category) => {
                const allChild: CategoryNode = {
                    ...category,
                    name: intl.formatMessage(allCategory, { categoryName: category.name }),
                    children: [],
                };
                return {
                    ...category,
                    children: [allChild, ...(category.children ?? [])],
                };
            }),
        [filters?.categories, intl],
    );

    const openCategoryIndex = useMemo(() => {
        if (!openCategoryId) return -1;
        return fullCategories.findIndex((category) => category.id === openCategoryId);
    }, [fullCategories, openCategoryId]);

    const handleAccordionChange = useCallback(
        (index: number | number[]) => {
            const actualIndex = Array.isArray(index) ? index[0] : index;
            if (actualIndex === -1 || actualIndex === undefined) {
                setOpenCategoryId(null);
            } else {
                setOpenCategoryId(fullCategories[actualIndex]?.id || null);
            }
        },
        [fullCategories],
    );

    const handleCategoryChange = useCallback(
        (category: CategoryNode, isChecked: boolean) => {
            const { type: selectedCategoryType, parent: parentId } = categoriesMap[category.id];

            if (!isChecked) {
                onChangeFilter('category', category.id);
                return;
            }

            if (selectedCategoryType === 'T2') {
                // Get current active category filters
                const activeCategoryFilters = activeFilters.filter(
                    (filter) => filter.type === 'category',
                );

                // Keep other selected T2 categories and T3s that are not children of the selected T2
                const filtersToKeep = activeCategoryFilters.filter((filter) => {
                    const filterCategoryType = categoriesMap[filter.value]?.type;
                    if (filterCategoryType === 'T2') {
                        // Keep other T2 categories (avoid duplicating the just-selected one)
                        return filter.value !== category.id;
                    }
                    if (filterCategoryType === 'T3') {
                        // Keep T3 only if it is not a child of the newly selected T2
                        const isChildOfSelected =
                            categoriesMap[filter.value]?.parent === category.id;
                        return !isChildOfSelected;
                    }
                    return false;
                });

                // Add the selected T2 category and keep the filtered T2/T3 categories
                const newFilters = [
                    { type: 'category', value: category.id, label: category.name },
                    ...filtersToKeep,
                ];

                onBulkChangeFilter(newFilters);
                return;
            }

            if (selectedCategoryType === 'T3') {
                // If the parent T2 is selected, remove it from the filters
                const activeCategoryFilters = activeFilters.filter(
                    (filter) => filter.type === 'category',
                );
                const parentIsSelected = !!(
                    parentId && activeCategoryFilters.find((f) => f.value === parentId)
                );
                if (parentIsSelected) {
                    // Remove the parent T2 and add/toggle the T3
                    const filtersWithoutParent = activeCategoryFilters.filter(
                        (f) => f.value !== parentId,
                    );
                    // Toggle the T3: if it's already selected, remove it; otherwise, add it
                    const t3IsSelected = !!activeCategoryFilters.find(
                        (f) => f.value === category.id,
                    );
                    let newFilters = [...filtersWithoutParent];
                    if (!t3IsSelected) {
                        newFilters.push({
                            type: 'category',
                            value: category.id,
                            label: category.name,
                        });
                    } else {
                        newFilters = newFilters.filter((f) => f.value !== category.id);
                    }
                    onBulkChangeFilter(newFilters);
                    return;
                }
                onChangeFilter('category', category.id);
            }
        },
        [activeFilters, categoriesMap, onBulkChangeFilter, onChangeFilter],
    );

    const isLastLevel = useMemo(
        () => filters?.categories.every((category) => (category.children?.length ?? 0) === 0),
        [filters?.categories],
    );

    return isLastLevel ? (
        <ListReveal<CategoryNode>
            data={filters?.categories ?? []}
            keyExtractor={(child) => child.id}
            initialNumToRender={5}
            listContainerClassName="flex flex-col gap-4 ml-[2px]"
            renderItem={({ item: childCategory }) => (
                <Checkbox
                    key={childCategory.id}
                    isChecked={isFilterSelected('category', childCategory.id)}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        handleCategoryChange(childCategory, event.target.checked)
                    }
                    isDisabled={childCategory.count === 0}
                    variant="new-style"
                    data-testid={`category-filter-checkbox-${childCategory.id}`}
                >
                    {childCategory.name} ({childCategory.count})
                </Checkbox>
            )}
        />
    ) : (
        <Accordion
            id="category-filter-accordion"
            index={openCategoryIndex}
            onChange={handleAccordionChange}
            variant="plp-categories"
        >
            {fullCategories.map((category) => (
                <AccordionItem
                    key={category.id}
                    id={`category-filter-${category.id}`}
                    title={category.name}
                    collapsedIcon="plus"
                    expandedIcon="minus"
                    borderColor="transparent"
                >
                    <div className="ml-3">
                        {category.children && category.children.length > 0 ? (
                            <ListReveal<CategoryNode>
                                data={category.children}
                                keyExtractor={(child) => child.id}
                                initialNumToRender={5}
                                listContainerClassName="flex flex-col gap-4 ml-[2px]"
                                renderItem={({ item: childCategory }) => (
                                    <Checkbox
                                        key={childCategory.id}
                                        isChecked={isFilterSelected('category', childCategory.id)}
                                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                            handleCategoryChange(
                                                childCategory,
                                                event.target.checked,
                                            )
                                        }
                                        isDisabled={childCategory.count === 0}
                                        variant="new-style"
                                        data-testid={`category-filter-checkbox-${childCategory.id}`}
                                    >
                                        {childCategory.name} ({childCategory.count})
                                    </Checkbox>
                                )}
                            />
                        ) : null}
                    </div>
                </AccordionItem>
            ))}
        </Accordion>
    );
};
